import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TrendingDown, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const DepensePortefeuilleDialog = ({ open, onClose, portefeuille, onSuccess }) => {
  const [montant, setMontant] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!montant || parseFloat(montant) <= 0) {
      toast.error('Le montant doit être positif');
      return;
    }
    if (parseFloat(montant) > portefeuille.solde) {
      toast.error('Solde insuffisant');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/portefeuilles/${portefeuille.id}/depenser`,
        { montant: parseFloat(montant), description: description.trim() || null },
        { withCredentials: true }
      );
      toast.success('Dépense enregistrée !');
      onSuccess();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMontant('');
    setDescription('');
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const insufficientFunds = montant && parseFloat(montant) > portefeuille.solde;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <TrendingDown className="h-5 w-5" />
            Dépenser
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 text-center">
            <div className="text-sm text-amber-700 mb-1">Solde disponible</div>
            <div className="text-2xl font-bold text-amber-900">{formatCurrency(portefeuille.solde)}</div>
          </div>

          <div>
            <Label htmlFor="montant">Montant dépensé (€) *</Label>
            <Input
              id="montant"
              type="number"
              step="0.01"
              value={montant}
              onChange={(e) => setMontant(e.target.value)}
              placeholder="0.00"
              required
              autoFocus
            />
            {insufficientFunds && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Solde insuffisant
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Description (optionnel)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Pour quoi ?"
              rows={3}
            />
          </div>

          {montant && parseFloat(montant) > 0 && !insufficientFunds && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
              <div className="text-sm text-blue-700 mb-2">Nouveau solde</div>
              <div className="text-2xl font-bold text-blue-900">
                {formatCurrency(portefeuille.solde - parseFloat(montant))}
              </div>
            </div>
          )}

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-600">
              ℹ️ Ne sera pas ajouté au calendrier (argent déjà déduit lors du dépôt)
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1" disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" className="flex-1 bg-amber-600" disabled={loading || insufficientFunds}>
              {loading ? 'Dépense...' : 'Confirmer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DepensePortefeuilleDialog;